import React, { useState, useEffect, useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import {
  getInternalSEColor,
  shortenNarrativeLabel,
  buildChartTheme,
  subtleHorizontalSplitLine,
  truncateChartLabel,
} from './chartTheme';
import useLaptopCompact from './useLaptopCompact';
import ChartEmptyState from './ChartEmptyState';

const RegionalSENarrativeBreakdown = ({
  data,
  exportId = 'export-internal-regional-se-narrative-breakdown',
  renderZeroWhenEmpty = false,
  zeroRegion = 'APJ',
  zeroSeriesName = 'Narrative Runs',
  emptyStateTitle = 'No activity for this period',
  emptyStateSubtitle = 'Try changing the time filter.',
  emptyStateCompact = false,
}) => {
  const isLaptopCompact = useLaptopCompact();

  const regionsWithData = useMemo(
    () =>
      Object.keys(data || {}).filter(
        (r) => data[r] && Object.keys(data[r]).length > 0
      ),
    [data]
  );
  const effectiveRegions = regionsWithData.length
    ? regionsWithData
    : (renderZeroWhenEmpty ? [zeroRegion] : []);

  const [selectedRegion, setSelectedRegion] = useState('APJ');

  useEffect(() => {
    if (effectiveRegions.length && !effectiveRegions.includes(selectedRegion)) {
      setSelectedRegion(effectiveRegions[0]);
    }
  }, [effectiveRegions, selectedRegion]);

  const chartOptions = useMemo(() => {
    const theme = buildChartTheme(isLaptopCompact);
    const regionData = data?.[selectedRegion];
    const hasRegionData = !!(regionData && Object.keys(regionData).length);
    if (!hasRegionData && !renderZeroWhenEmpty) return null;
    const normalizedRegionData = hasRegionData
      ? regionData
      : {
          'SE 1': {
            Total: 0,
            Narratives: [{ Narrative: zeroSeriesName, Count: 0 }],
          },
          'SE 2': {
            Total: 0,
            Narratives: [{ Narrative: zeroSeriesName, Count: 0 }],
          },
          'SE 3': {
            Total: 0,
            Narratives: [{ Narrative: zeroSeriesName, Count: 0 }],
          },
        };

    const seNames = Object.keys(normalizedRegionData);
    const seTotals = seNames
      .map((name) => ({
        name,
        total: normalizedRegionData[name]?.Total || 0,
      }))
      .sort((a, b) => b.total - a.total);

    const sortedSEs = seTotals.map((s) => s.name);
    const narrativeSet = new Set();
    sortedSEs.forEach((se) => {
      (normalizedRegionData[se]?.Narratives || []).forEach((n) =>
        narrativeSet.add(n.Narrative)
      );
    });
    const narratives = Array.from(narrativeSet);
    const isAllZero = sortedSEs.every((se) => (normalizedRegionData[se]?.Total || 0) === 0);

    const series = narratives.map((narrative, idx) => ({
      name: narrative,
      type: 'bar',
      stack: 'total',
      barMaxWidth: 48,
      data: sortedSEs.map((se) => {
        const item = (normalizedRegionData[se]?.Narratives || []).find(
          (n) => n.Narrative === narrative
        );
        return item?.Count || 0;
      }),
      minPointSize: isAllZero ? 2 : 0,
      itemStyle: { color: getInternalSEColor(idx) },
    }));

    const legendBottom = sortedSEs.length > 6 ? 98 : 84;
    const chartHeight = Math.min(420, Math.max(300, 120 + sortedSEs.length * 8));

    return {
      options: {
        animation: false,
        grid: {
          ...theme.gridWithLegend,
          bottom: isLaptopCompact ? Math.round(legendBottom * 0.88) : legendBottom,
        },
        tooltip: {
          ...theme.tooltipBase,
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
        },
        legend: {
          ...theme.legendBase,
          type: 'scroll',
          bottom: 0,
          left: 'center',
          right: 'center',
          itemGap: isLaptopCompact ? 12 : 14,
          formatter: (name) => truncateChartLabel(shortenNarrativeLabel(name), 18),
        },
        xAxis: {
          type: 'category',
          data: sortedSEs,
          axisLabel: {
            ...theme.axisLabel,
            rotate: sortedSEs.length > 5 ? 20 : 0,
            formatter: (value) => truncateChartLabel(value, 16),
            margin: 14,
          },
        },
        yAxis: {
          type: 'value',
          name: 'Narrative Runs',
          nameTextStyle: { fontSize: theme.fs(12), color: '#6B7280' },
          axisLabel: theme.axisLabel,
          min: 0,
          max: isAllZero ? 1 : undefined,
          splitLine: subtleHorizontalSplitLine,
        },
        series,
      },
      height: isLaptopCompact ? Math.round(chartHeight * 0.94) : chartHeight,
    };
  }, [data, selectedRegion, isLaptopCompact, renderZeroWhenEmpty, zeroSeriesName]);

  if (!effectiveRegions.length) {
    return (
      <ChartEmptyState
        title={emptyStateTitle}
        subtitle={emptyStateSubtitle}
        compact={emptyStateCompact}
      />
    );
  }

  return (
    <div id={exportId} className="lead-dash-card lead-dash-card-full">
      <div className="lead-dash-card-header">
        <h3 className="lead-dash-card-title">Regional SE Narrative Runs Breakdown</h3>
        <select
          className="lead-dash-select"
          value={selectedRegion}
          onChange={(e) => setSelectedRegion(e.target.value)}
          aria-label="Select region"
        >
          {effectiveRegions.map((region) => (
            <option key={region} value={region}>
              {region}
            </option>
          ))}
        </select>
      </div>
      <div className="lead-dash-card-body">
        {chartOptions ? (
          <ReactECharts
            option={chartOptions.options}
            style={{ height: `${chartOptions.height}px`, width: '100%' }}
            opts={{ renderer: 'canvas' }}
          />
        ) : (
          <ChartEmptyState
            title={emptyStateTitle}
            subtitle={emptyStateSubtitle}
            compact={emptyStateCompact}
          />
        )}
      </div>
    </div>
  );
};

export default RegionalSENarrativeBreakdown;
